import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { processService } from "../../services/process.service";
import { userService } from "../../services/user.service";
import { useFormStore } from "../../store/useFormStore";
import { mapDataToForm } from "../../lib/utils";
import bancos from "../../lib/bancos.json";
import { Card } from "../ui/card";
import { toast } from "sonner";
import { CedentesList } from "./cedentes/CedentesList";
import { CedenteActions } from "./cedentes/CedenteActions";
import { CedenteDialog } from "./cedentes/CedenteDialog";
import { CedenteEditDialog } from "./cedentes/CedenteEditDialog";
import { AssinaturaManualDialog } from "./cedentes/AssinaturaManualDialog";
import { HerdeiroDialog } from "./cedentes/HerdeiroDialog";
import { ExportColumnsDialog } from "./cedentes/ExportColumnsDialog";
import useDebounce from "../../hooks/useDebounce";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";

const { importCedentesCSV } = userService;
const { unlinkCedente, getProcessoCedentes, getTranches, assignTranche } = processService;

export function CedentesTab({ processoId, temValores = true, camposValores = [], parcelado = true, calculoAutomatico = true, tipoAceitePadrao = null }) {
    const navigate = useNavigate();
    const { setUserData, setBancoData } = useFormStore();
    const [processoCedentes, setProcessoCedentes] = useState([]);
    const [loadingCedentes, setLoadingCedentes] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [linkedSearch, setLinkedSearch] = useState("");
    const debouncedLinkedSearch = useDebounce(linkedSearch, 1000);
    const [filterTipoAceite, setFilterTipoAceite] = useState(null);
    const [filterStatus, setFilterStatus] = useState(null);
    const [filterTranche, setFilterTranche] = useState(null);
    const [availableTranches, setAvailableTranches] = useState([]);

    const [isCedenteDialogOpen, setIsCedenteDialogOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isImportingXLSX, setIsImportingXLSX] = useState(false);
    const [criarSeNaoExistir, setCriarSeNaoExistir] = useState(false);
    const [resetarAssinados, setResetarAssinados] = useState(false);
    const [definirAtivo, setDefinirAtivo] = useState(null);
    const [editingDados, setEditingDados] = useState(null);
    const [isExporting, setIsExporting] = useState(false);
    const [showExportDialog, setShowExportDialog] = useState(false);
    const [manualDialogCedente, setManualDialogCedente] = useState(null);
    const [manualIds, setManualIds] = useState(new Set());
    const [isImportingManual, setIsImportingManual] = useState(false);
    const [herdeiroDialogCedente, setHerdeiroDialogCedente] = useState(null);
    const [xlsxImportResult, setXlsxImportResult] = useState(null);
    const [syncingEnvelopeId, setSyncingEnvelopeId] = useState(null);

    const loadManualIds = useCallback(async () => {
        if (!processoId) return;
        try {
            const items = await processService.listManualSignatures(processoId);
            setManualIds(new Set(items.map(i => i.cedente_id)));
        } catch (_) {}
    }, [processoId]);

    const loadProcessoCedentes = useCallback(async () => {
        if (!processoId) return;
        try {
            setLoadingCedentes(true);
            const offset = (page - 1) * pageSize;
            const data = await getProcessoCedentes(processoId, debouncedLinkedSearch, pageSize, offset, filterTipoAceite, filterStatus, filterTranche);
            setProcessoCedentes(data.items);
            setTotalItems(data.total);
        } catch (e) {
            toast.error("Erro ao carregar substituídos vinculados.");
        } finally {
            setLoadingCedentes(false);
        }
    }, [processoId, page, pageSize, debouncedLinkedSearch, filterTipoAceite, filterStatus, filterTranche]);

    const loadTranches = useCallback(async () => {
        if (!processoId) return;
        try {
            const data = await getTranches(processoId);
            setAvailableTranches(data);
        } catch (_) {}
    }, [processoId]);

    useEffect(() => {
        loadProcessoCedentes();
        loadManualIds();
    }, [loadProcessoCedentes, loadManualIds, page, pageSize]);

    useEffect(() => {
        loadTranches();
    }, [loadTranches]);

    useEffect(() => {
        setPage(1);
    }, [debouncedLinkedSearch]);

    useEffect(() => {
        setPage(1);
    }, [filterTipoAceite, filterStatus, filterTranche]);

    const handleUnlinkCedente = useCallback(async (cedenteId) => {
        if (!window.confirm("Tem certeza que deseja desvincular este substituído?")) return;
        try {
            await unlinkCedente(processoId, cedenteId);
            toast.success("Substituído desvinculado");
            loadProcessoCedentes();
        } catch (e) {
            toast.error("Erro ao desvincular substituído");
        }
    }, [processoId, loadProcessoCedentes]);

    const handleBulkUnlink = useCallback(async (cedenteIds) => {
        if (!window.confirm(`Tem certeza que deseja desvincular ${cedenteIds.length} substituídos selecionados?`)) return;
        try {
            await processService.unlinkCedentesBulk(processoId, cedenteIds);
            toast.success(`${cedenteIds.length} substituídos desvinculados com sucesso!`);
            loadProcessoCedentes();
        } catch (e) {
            toast.error("Erro ao desvincular substituídos em lote: " + e.message);
        }
    }, [processoId, loadProcessoCedentes]);

    const handleImportCSV = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsSaving(true);
        try {
            await importCedentesCSV(processoId, file);
            toast.success("CSV importado com sucesso!");
            loadProcessoCedentes();
        } catch (e) {
            toast.error("Erro ao importar CSV: " + e.message);
        } finally {
            setIsSaving(false);
            e.target.value = null;
        }
    };

    const handleImportXLSX = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsImportingXLSX(true);
        try {
            const result = await processService.importCedentesXLSX(processoId, file, criarSeNaoExistir, resetarAssinados, definirAtivo);
            setXlsxImportResult(result);
            loadProcessoCedentes();
        } catch (err) {
            toast.error("Erro ao importar propostas: " + (err.response?.data?.detail || err.message));
        } finally {
            setIsImportingXLSX(false);
            e.target.value = null;
        }
    };

    const handleImportManualXLSX = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setIsImportingManual(true);
        try {
            const result = await processService.importManualSignatures(processoId, file);
            const notFoundMsg = result.not_found?.length
                ? ` Não encontrados: ${result.not_found.slice(0, 5).join(", ")}${result.not_found.length > 5 ? ` e mais ${result.not_found.length - 5}` : ""}.`
                : "";
            toast.success(`${result.imported} assinaturas registradas, ${result.skipped} já existiam.${notFoundMsg}`);
            loadManualIds();
            loadProcessoCedentes();
        } catch (err) {
            toast.error("Erro ao importar assinaturas manuais: " + (err.response?.data?.detail || err.message));
        } finally {
            setIsImportingManual(false);
            e.target.value = null;
        }
    };

    const handleViewAsCedente = useCallback(async (cedenteId) => {
        try {
            const data = await userService.getUserById(cedenteId);
            const mapped = mapDataToForm(data);
            const bank = bancos.find((b) => b.nome === data.banco);
            setBancoData({
                nomeBanco: bank ? `${bank.codigo} - ${data.banco}` : data.banco || "",
                agencia: data.agencia || "",
                conta: data.conta_corrente ? `${data.conta_corrente}-${data.digito_conta}` : "",
                tipoPix: data.tipo_pix || "cpf",
                chavePix: data.pix || "",
                tipoConta: data.tipo_conta || "corrente",
            });
            setUserData(mapped);
            navigate("/form");
        } catch (err) {
            toast.error("Erro ao abrir visão do cedente: " + err.message);
        }
    }, [navigate, setUserData, setBancoData]);

    const handleToggleAtivo = async (cedenteId) => {
        try {
            await processService.toggleCedenteAtivo(cedenteId);
            loadProcessoCedentes();
        } catch (err) {
            toast.error("Erro ao alterar status: " + err.message);
        }
    };

    const handleBulkInativar = async (cedenteIds) => {
        if (!window.confirm(`Inativar ${cedenteIds.length} cedente(s)? Eles perderão acesso à plataforma.`)) return;
        try {
            await processService.bulkSetAtivoByIds(cedenteIds, false);
            toast.success(`${cedenteIds.length} cedente(s) inativados.`);
            loadProcessoCedentes();
        } catch (err) {
            toast.error("Erro ao inativar: " + err.message);
        }
    };

    const handleTogglePendenteDocumentacao = (cedenteId, atualPendente) => {
        const acao = atualPendente ? "remover a pendência de documentação" : "marcar como pendente de documentação";
        if (!window.confirm(`Confirma ${acao} deste cedente?`)) return;
        processService.toggleCedentePendenteDocumentacao(cedenteId)
            .then(loadProcessoCedentes)
            .catch(err => toast.error("Erro ao alterar pendência de documentação: " + err.message));
    };

    const handleToggleRecalculo = (cedenteId, atualRecalculo) => {
        const acao = atualRecalculo
            ? "liberar esta proposta (remover o recálculo)"
            : "marcar esta proposta como em recálculo. O cedente não poderá aceitá-la e será obrigado a enviar documentos";
        if (!window.confirm(`Confirma ${acao}?`)) return;
        processService.toggleProcessoCedenteRecalculo(processoId, cedenteId)
            .then(loadProcessoCedentes)
            .catch(err => toast.error("Erro ao alterar status de recálculo: " + err.message));
    };

    const handleSyncEnvelope = async (cedenteId, processoCedenteProcessoId, envelopeKeyOverride = null) => {
        const pid = processoCedenteProcessoId || processoId;
        setSyncingEnvelopeId(cedenteId);
        try {
            const result = await processService.syncCedenteEnvelope(pid, cedenteId, envelopeKeyOverride);
            if (result.assinado) {
                toast.success("Assinatura confirmada no ClickSign. Status atualizado.");
            } else if (result.action === "not_found") {
                const key = window.prompt(
                    "Envelope não encontrado automaticamente.\n\nCole o UUID do envelope ClickSign para vincular manualmente (ou cancele para ignorar):"
                );
                if (key && key.trim()) {
                    setSyncingEnvelopeId(null);
                    await handleSyncEnvelope(cedenteId, pid, key.trim());
                    return;
                }
                toast.warning("Envelope não encontrado. Informe o UUID manualmente se necessário.");
            } else {
                toast.info(result.message || "Envelope sincronizado.");
            }
            await loadProcessoCedentes();
        } catch (err) {
            toast.error("Erro ao sincronizar envelope: " + (err.response?.data?.detail || err.message));
        } finally {
            setSyncingEnvelopeId(null);
        }
    };

    const handleToggleFalecido = (cedenteId, atualFalecido, cedente) => {
        if (atualFalecido) {
            if (!window.confirm("Confirma reverter o status de falecido? O cedente voltará a ter acesso à plataforma.")) return;
            processService.toggleCedenteFalecido(cedenteId)
                .then(loadProcessoCedentes)
                .catch(err => toast.error("Erro ao alterar status de falecido: " + err.message));
        } else {
            setHerdeiroDialogCedente(cedente || { id: cedenteId });
        }
    };

    const handleHerdeiroDialogConfirm = async () => {
        if (!herdeiroDialogCedente) return;
        try {
            await processService.toggleCedenteFalecido(herdeiroDialogCedente.id);
            setHerdeiroDialogCedente(null);
            loadProcessoCedentes();
        } catch (err) {
            toast.error("Erro ao marcar como falecido: " + err.message);
        }
    };

    const handleRegisterManual = useCallback((cedente) => {
        setManualDialogCedente(cedente);
    }, []);

    const handleAssignTranche = useCallback(async (cedenteIds, tranche) => {
        try {
            await assignTranche(processoId, cedenteIds, tranche);
            toast.success(tranche ? `Tranche "${tranche}" atribuída` : "Tranche removida");
            loadProcessoCedentes();
            loadTranches();
        } catch {
            toast.error("Erro ao atribuir tranche");
        }
    }, [processoId, loadProcessoCedentes, loadTranches]);

    const handleEdit = useCallback((processoCedente) => {
        setEditingItem(processoCedente);
        setIsCedenteDialogOpen(true);
    }, []);

    const handleEditDados = useCallback((cedente) => {
        setEditingDados(cedente);
    }, []);

    const handleDialogOpenChange = useCallback((open) => {
        setIsCedenteDialogOpen(open);
        if (!open) setEditingItem(null);
    }, []);

    const handleExportCSV = useCallback(async () => {
        if (!processoId) return;
        setIsExporting(true);
        try {
            const blob = await processService.exportProcessoCedentesCSV(processoId);
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.setAttribute("href", url);
            link.setAttribute("download", `cedentes_processo_${processoId}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error("Erro ao exportar: " + e.message);
        } finally {
            setIsExporting(false);
        }
    }, [processoId]);

    const handleExportXLSX = useCallback(async (selectedColumns) => {
        if (!processoId) return;
        setIsExporting(true);
        try {
            const blob = await processService.exportProcessoCedentesXLSX(processoId, selectedColumns);
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `cedentes_processo_${processoId}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            setShowExportDialog(false);
        } catch (e) {
            toast.error("Erro ao exportar: " + e.message);
        } finally {
            setIsExporting(false);
        }
    }, [processoId]);

    const handleDownloadTemplate = useCallback(async () => {
        try {
            const blob = await processService.downloadTemplateCedentes(processoId);
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `modelo_cedentes_${processoId}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error("Erro ao baixar modelo: " + e.message);
        }
    }, [processoId]);

    const handleDownloadTemplatePropostas = useCallback(async () => {
        try {
            const blob = await processService.downloadTemplatePropostas(processoId);
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `modelo_propostas_${processoId}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error("Erro ao baixar modelo: " + e.message);
        }
    }, [processoId]);

    const handleDownloadTemplateManuais = useCallback(async () => {
        try {
            const blob = await processService.downloadTemplateAssinaturasManuais(processoId);
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `modelo_assinaturas_manuais_${processoId}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error("Erro ao baixar modelo: " + e.message);
        }
    }, [processoId]);

    return (
        <Card className="p-6">
            <ExportColumnsDialog
                open={showExportDialog}
                onClose={() => setShowExportDialog(false)}
                onExport={handleExportXLSX}
                camposValores={camposValores}
                isExporting={isExporting}
            />
            <CedenteActions
                onDownloadTemplate={handleDownloadTemplate}
                onDownloadTemplatePropostas={handleDownloadTemplatePropostas}
                onDownloadTemplateManuais={handleDownloadTemplateManuais}
                onImportCSV={handleImportCSV}
                onImportXLSX={handleImportXLSX}
                onOpenDialog={setIsCedenteDialogOpen}
                isSaving={isSaving}
                isImportingXLSX={isImportingXLSX}
                onExportCSV={handleExportCSV}
                onExportXLSX={() => setShowExportDialog(true)}
                isExporting={isExporting}
                onImportManualXLSX={handleImportManualXLSX}
                isImportingManual={isImportingManual}
                criarSeNaoExistir={criarSeNaoExistir}
                onToggleCriar={setCriarSeNaoExistir}
                resetarAssinados={resetarAssinados}
                onToggleResetar={setResetarAssinados}
                definirAtivo={definirAtivo}
                onSetDefinirAtivo={setDefinirAtivo}
            />

            <CedentesList
                cedentes={processoCedentes}
                loading={loadingCedentes}
                totalItems={totalItems}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                onUnlink={handleUnlinkCedente}
                onBulkUnlink={handleBulkUnlink}
                onBulkInativar={handleBulkInativar}
                onEdit={handleEdit}
                onEditDados={handleEditDados}
                searchQuery={linkedSearch}
                onSearch={setLinkedSearch}
                isDebouncing={linkedSearch !== debouncedLinkedSearch}
                temValores={temValores}
                manualIds={manualIds}
                onRegisterManual={handleRegisterManual}
                onToggleAtivo={handleToggleAtivo}
                onToggleFalecido={handleToggleFalecido}
                onTogglePendenteDocumentacao={handleTogglePendenteDocumentacao}
                onToggleRecalculo={handleToggleRecalculo}
                tipoAceitePadrao={tipoAceitePadrao}
                onViewAsCedente={handleViewAsCedente}
                filterTipoAceite={filterTipoAceite}
                onFilterTipoAceite={setFilterTipoAceite}
                filterStatus={filterStatus}
                onFilterStatus={setFilterStatus}
                filterTranche={filterTranche}
                onFilterTranche={setFilterTranche}
                availableTranches={availableTranches}
                onAssignTranche={handleAssignTranche}
                onSyncEnvelope={handleSyncEnvelope}
                syncingEnvelopeId={syncingEnvelopeId}
                onRefresh={loadProcessoCedentes}
            />

            <CedenteEditDialog
                isOpen={!!editingDados}
                onOpenChange={(open) => { if (!open) setEditingDados(null); }}
                cedente={editingDados}
                onSuccess={loadProcessoCedentes}
            />

            <AssinaturaManualDialog
                isOpen={!!manualDialogCedente}
                onOpenChange={(open) => { if (!open) setManualDialogCedente(null); }}
                processoId={processoId}
                cedente={manualDialogCedente}
                onSuccess={() => { loadManualIds(); loadProcessoCedentes(); }}
            />

            <CedenteDialog
                key={editingItem?.id ?? 'new'}
                isOpen={isCedenteDialogOpen}
                onOpenChange={handleDialogOpenChange}
                processoId={processoId}
                onSuccess={loadProcessoCedentes}
                editingItem={editingItem}
                temValores={temValores}
                camposValores={camposValores}
                parcelado={parcelado}
                calculoAutomatico={calculoAutomatico}
                tipoAceitePadrao={tipoAceitePadrao}
            />

            <HerdeiroDialog
                isOpen={!!herdeiroDialogCedente}
                onOpenChange={(open) => { if (!open) setHerdeiroDialogCedente(null); }}
                cedente={herdeiroDialogCedente}
                onConfirm={handleHerdeiroDialogConfirm}
            />

            <XlsxImportResultDialog
                result={xlsxImportResult}
                onClose={() => setXlsxImportResult(null)}
            />
        </Card>
    );
}

function XlsxImportResultDialog({ result, onClose }) {
    if (!result) return null;

    const hasNameMismatch = result.name_mismatch?.length > 0;
    const hasNotFound = result.not_found?.length > 0;

    return (
        <Dialog open={!!result} onOpenChange={(open) => { if (!open) onClose(); }}>
            <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>Resultado da Importacao</DialogTitle>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto space-y-4">
                    {/* Resumo */}
                    <div className="flex flex-wrap gap-3">
                        <SummaryBadge color="emerald" label="Importados" value={result.imported ?? 0} />
                        {result.created > 0 && (
                            <SummaryBadge color="blue" label="Criados" value={result.created} />
                        )}
                        <SummaryBadge color="slate" label="Ignorados" value={result.skipped ?? 0} />
                        {hasNotFound && (
                            <SummaryBadge color="red" label="Nao encontrados" value={result.not_found.length} />
                        )}
                        {hasNameMismatch && (
                            <SummaryBadge color="amber" label="Nome divergente" value={result.name_mismatch.length} />
                        )}
                    </div>

                    {/* Tabela de divergencias de nome */}
                    {hasNameMismatch && (
                        <div>
                            <h4 className="text-sm font-semibold text-statusWarning mb-2">
                                Cedentes importados com nome diferente do sistema
                            </h4>
                            <div className="rounded-md border border-statusWarning/20 overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead className="bg-statusWarningBg text-statusWarning text-xs uppercase">
                                        <tr>
                                            <th className="px-3 py-2 text-left">CPF</th>
                                            <th className="px-3 py-2 text-left">Nome na planilha</th>
                                            <th className="px-3 py-2 text-left">Nome no sistema</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-amber-100">
                                        {result.name_mismatch.map((item, i) => (
                                            <tr key={i} className="bg-card">
                                                <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{item.cpf}</td>
                                                <td className="px-3 py-2 text-foreground">{item.nome_planilha}</td>
                                                <td className="px-3 py-2 text-muted-foreground">{item.nome_sistema}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Lista de nao encontrados */}
                    {hasNotFound && (
                        <div>
                            <h4 className="text-sm font-semibold text-statusError mb-2">
                                Nao encontrados ({result.not_found.length})
                            </h4>
                            <ul className="rounded-md border border-statusError/20 bg-statusErrorBg px-4 py-3 space-y-1 max-h-40 overflow-y-auto">
                                {result.not_found.map((item, i) => (
                                    <li key={i} className="text-sm text-statusError">{item}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button onClick={onClose}>Fechar</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function SummaryBadge({ color, label, value }) {
    const colors = {
        emerald: "bg-statusSuccessBg border-statusSuccess/20 text-statusSuccess",
        blue: "bg-statusInfoBg border-statusInfo/20 text-statusInfo",
        slate: "bg-muted border-border text-foreground",
        red: "bg-statusErrorBg border-statusError/20 text-statusError",
        amber: "bg-statusWarningBg border-statusWarning/20 text-statusWarning",
    };
    return (
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium ${colors[color] || colors.slate}`}>
            <span className="text-xl font-bold">{value}</span>
            <span>{label}</span>
        </div>
    );
}
